/**
 * File: serverDocumentService.ts
 * Path: apps/admin/src/shared/services/serverDocumentService.ts
 * Description: The PDF the server rendered, turned back into a downloadable
 * blob (P3.4).
 *
 * The admin tool used to render its own second copy of every document in the
 * browser, from the same inputs the server had already rendered from. Two
 * renderers meant the file the team filed could differ from the one the
 * customer received — different react-pdf versions, different fonts, a
 * different reference code.
 */

import type { RenderedDocument } from "@idk/config";

export const PDF_MIME = "application/pdf";

/** Decodes base64 to bytes without pulling in a polyfill. */
export const base64ToBytes = (base64: string): Uint8Array => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

export const base64ToPdfBlob = (base64: string): Blob =>
  // `.buffer` keeps TS happy across lib.dom versions, where Uint8Array is not
  // always assignable to BlobPart directly.
  new Blob([base64ToBytes(base64).buffer as ArrayBuffer], { type: PDF_MIME });

/**
 * True when the response carries a usable document. A malformed or empty one
 * falls back to the local render rather than downloading a broken file.
 */
export const hasRenderedDocument = (
  value: unknown,
): value is RenderedDocument => {
  const doc = value as RenderedDocument | undefined;
  return (
    !!doc &&
    typeof doc.base64 === "string" &&
    doc.base64.length > 0 &&
    typeof doc.bytes === "number" &&
    doc.bytes > 0
  );
};
